import React, { useEffect, useState, useRef } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/upr-api";
import { CheckCircle2, XCircle, Loader2, Download } from "lucide-react";
import { useCart } from "@/contexts/CartContext";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function MerchSuccess() {
  const navigate = useNavigate();
  const q = useQuery();
  const sessionId = q.get("session_id");
  const [state, setState] = useState({ status: "checking", message: "Checking payment status…", data: null });
  const [grant, setGrant] = useState(null);
  const attempts = useRef(0);
  const { clear: clearCart } = useCart();
  // The status endpoint is polled multiple times — guard against clearing the
  // cart more than once (harmless, but keeps intent explicit).
  const cartCleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", message: "No session ID provided.", data: null });
      return;
    }
    let cancelled = false;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ status: "paid", message: "Payment successful — thank you!", data });
          // Purchase confirmed — flush the local cart so the drawer / navbar
          // count reset and the customer doesn't see stale items on the way
          // back to the shop.
          if (!cartCleared.current) {
            cartCleared.current = true;
            clearCart();
          }
          if (data.download?.token) {
            try {
              const { data: g } = await api.get(`/downloads/${data.download.token}`);
              if (!cancelled) setGrant(g);
            } catch (e) {
              console.warn("[merch-success] download grant not ready yet:", e?.response?.status);
            }
          }
          return;
        }
        if (data.payment_status === "expired" || data.payment_status === "failed") {
          setState({ status: "failed", message: `Payment ${data.payment_status}.`, data });
          return;
        }
        if (attempts.current >= 8) {
          setState({ status: "pending", message: "Still processing. We'll email you when it's confirmed.", data });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        if (cancelled) return;
        setState({ status: "error", message: formatApiError(e.response?.data?.detail) || "Could not verify payment.", data: null });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const Icon = state.status === "paid" ? CheckCircle2
    : state.status === "failed" || state.status === "error" ? XCircle
    : Loader2;
  const accent = state.status === "paid" ? "var(--upr-secondary)"
    : (state.status === "failed" || state.status === "error") ? "var(--upr-primary)"
    : "var(--upr-muted)";

  const downloadHref = grant ? `${window.location.origin}/merch/success?session_id=${sessionId}` : "";

  const copyLink = async () => {
    if (!downloadHref) return;
    try {
      await navigator.clipboard.writeText(downloadHref);
    } catch (e) {
      console.warn("[merch-success] copy link failed:", e);
    }
  };

  return (
    <div className="px-6 md:px-12 lg:px-16 py-16 md:py-24 max-w-2xl mx-auto" data-testid="merch-success-page">
      <div className="flex items-center gap-3 mb-6" style={{ color: accent }}>
        <Icon size={32} className={state.status === "checking" ? "animate-spin" : ""} />
        <div className="upr-eyebrow">Order status</div>
      </div>
      <h1 className="font-heading font-black tracking-tighter leading-[0.9] text-4xl md:text-6xl" style={{ letterSpacing: "-0.035em" }} data-testid="merch-success-headline">
        {state.status === "paid" ? "Order confirmed." : state.status === "failed" ? "Payment failed." : state.status === "error" ? "Something went wrong." : "Confirming…"}
      </h1>
      <p className="mt-4 text-base md:text-lg" style={{ color: "var(--upr-muted)" }}>{state.message}</p>

      {state.data && state.status === "paid" && (
        <div className="mt-8 p-5 border" style={{ borderColor: "var(--upr-border)", background: "var(--upr-surface)" }}>
          <div className="upr-eyebrow mb-2">Receipt</div>
          <div className="font-heading font-extrabold text-xl">{state.data.merch_name}</div>
          {state.data.variant_label && (
            <div className="text-sm mt-0.5" style={{ color: "var(--upr-muted)" }}>Size: <strong>{state.data.variant_label}</strong></div>
          )}
          <div className="text-sm mt-1" style={{ color: "var(--upr-muted)" }}>
            {state.data.currency} {Number(state.data.amount || 0).toFixed(2)}
          </div>
        </div>
      )}

      {/* Bundled WAV download */}
      {grant && grant.tracks?.length > 0 && (
        <div
          className="mt-8 p-5 border"
          style={{ borderColor: "var(--upr-primary)", background: "var(--upr-bg)" }}
          data-testid="download-panel"
        >
          <div className="flex items-center gap-2 mb-3" style={{ color: "var(--upr-primary)" }}>
            <Download size={18} />
            <div className="upr-eyebrow" style={{ color: "var(--upr-primary)" }}>Included digital download</div>
          </div>
          <div className="font-heading font-extrabold text-xl leading-tight">{grant.release.title}</div>
          <div className="text-xs mt-0.5" style={{ color: "var(--upr-muted)" }}>
            {grant.release.cat_num} · High-quality MP3 · Link valid until {new Date(grant.expires_at).toLocaleDateString()}
          </div>
          <ul className="mt-4 space-y-2" data-testid="download-tracks">
            {grant.tracks.map((t, i) => (
              <li key={i} className="flex items-center justify-between gap-3 p-2.5 border" style={{ borderColor: "var(--upr-border)" }}>
                <span className="font-heading truncate">{i + 1}. {t.title || `Track ${i + 1}`}</span>
                <a
                  href={t.download_url}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="upr-btn"
                  data-testid={`download-track-${i}`}
                >
                  <Download size={12} /> Download
                </a>
              </li>
            ))}
          </ul>
          <button
            type="button"
            className="upr-btn upr-btn-ghost mt-4"
            onClick={copyLink}
            data-testid="copy-download-link"
          >
            Copy this page&apos;s link (save it — link valid 30 days)
          </button>
        </div>
      )}

      <div className="mt-10 flex flex-wrap gap-3">
        <Link to="/merch" className="upr-btn" data-testid="back-to-merch">Continue shopping</Link>
        <button onClick={() => navigate("/")} className="upr-btn upr-btn-ghost" data-testid="back-home">Back home</button>
        <Link to="/my-orders" className="upr-btn upr-btn-ghost ml-auto" data-testid="success-find-my-orders">
          Lost this page? Find my downloads →
        </Link>
      </div>
    </div>
  );
}
